/**
 * FMwvToolContext - React Context for FMwvTool
 * Provides access to all orchestrators following OpCentral patterns
 * 
 * CHANGE HISTORY:
 * - 251107_2145 Initial creation - Full OpCentral compliance upgrade
 * 
 * PATTERN: Use React hooks from orchestrator packages
 * - useActr() from @opcentral/actr
 * - useInterfacr() from @opcentral/interfacr
 * - useProfylr() from @opcentral/profylr
 * - useAggregatr() from @opcentral/aggregatr
 * - useIntegratr() from @opcentral/integratr
 * - useSynkr() from @opcentral/synkr
 */

import React, { createContext, useContext, ReactNode } from 'react';
import {
  useActr,
  useProfylr,
  useAggregatr,
  useIntegratr,
  useSynkr,
  getLoggr,
  ConsoleAdapter
} from '@opcentral/dependr';

export interface FMwvToolContextValue {
  // File operations
  loadFile: (file: File) => Promise<void>;
  saveFile: (data: any) => Promise<void>;
  clearFile: () => Promise<void>;
  
  // JSON extraction
  extractJson: (content: string) => Promise<any>;
  
  // FileMaker integration
  pingFileMaker: () => Promise<boolean>;
  sendToFileMaker: (data: any, script?: string) => Promise<void>;
  
  // Configuration
  getConfig: () => Promise<any>;
  updateConfig: (updates: any) => Promise<void>;
  
  // State
  currentFile: any | null;
  isInitialized: boolean;
}

const FMwvToolContext = createContext<FMwvToolContextValue | null>(null);

export interface FMwvToolProviderProps {
  children: ReactNode;
}

/**
 * FMwvToolProvider - Provides FMwvTool functionality via React Context
 * 
 * Wraps all 6 orchestrators and provides FMwvTool-specific operations
 */
export function FMwvToolProvider({ children }: FMwvToolProviderProps) {
  // Get all orchestrators via hooks
  const actr = useActr();
  const { profylr, activeProfile } = useProfylr();
  const aggregatr = useAggregatr();
  const integratr = useIntegratr();
  const synkr = useSynkr();
  const loggr = React.useMemo(() => getLoggr({ 
    category: 'fmwvtool-context',
    adapters: [new ConsoleAdapter({ minLevel: 'debug' })]
  }), []);

  const [currentFile, setCurrentFile] = React.useState<any | null>(null);
  const [isInitialized, setIsInitialized] = React.useState(false);

  // Initialize on mount
  React.useEffect(() => {
    const init = async () => {
      loggr.info('Initializing FMwvTool...');
      
      // Register actions with Actr
      actr.register('fmwvtool:load-file', {
        handler: async ({ file }: { file: File }) => {
          loggr.info('Loading file:', file.name);
          
          const content = await readFile(file);
          const fileData = {
            id: crypto.randomUUID(),
            name: file.name,
            type: file.type,
            size: file.size,
            content,
            timestamp: new Date().toISOString()
          };

          // Store in Aggregatr
          await aggregatr.create('file', fileData);

          setCurrentFile(fileData);
          
          // Notify via Loggr
          loggr.info(`Loaded ${file.name}`);

          return fileData;
        }
      });

      actr.register('fmwvtool:clear-file', {
        handler: async () => {
          setCurrentFile(null);
          loggr.info('File cleared');
        }
      });

      actr.register('fmwvtool:extract-json', {
        handler: async ({ content }: { content: string }) => {
          try {
            return JSON.parse(content);
          } catch (error) {
            loggr.error('JSON extraction failed:', error);
            throw new Error('Invalid JSON');
          }
        }
      });

      actr.register('fmwvtool:ping-filemaker', {
        handler: async () => {
          if (typeof window !== 'undefined' && (window as any).FileMaker) {
            loggr.info('✅ FileMaker bridge available');
            return true;
          }
          loggr.warn('❌ FileMaker bridge not available');
          return false;
        }
      });

      actr.register('fmwvtool:send-to-filemaker', {
        handler: async ({ data, script }: { data: any; script?: string }) => {
          const fm = (window as any).FileMaker;
          if (!fm) {
            throw new Error('FileMaker not available');
          }

          const json = JSON.stringify(data);
          if (script) {
            fm.PerformScript(script, json);
          }

          loggr.info('Sent data to FileMaker');
        }
      });

      actr.register('fmwvtool:get-config', {
        handler: async () => {
          // Get config from active profile context
          if (activeProfile && activeProfile.context?.fmwvtool) {
            return activeProfile.context.fmwvtool;
          }
          return {
            theme: 'auto',
            autoSave: true,
            fileMakerIntegration: true,
            syncEnabled: false
          };
        }
      });

      actr.register('fmwvtool:update-config', {
        handler: async ({ updates }: { updates: any }) => {
          // Get current config from profile
          const current = activeProfile?.context?.fmwvtool || {};
          const updated = { ...current, ...updates };
          
          // Update profile context
          if (activeProfile) {
            await profylr.update(activeProfile.id, {
              context: {
                ...activeProfile.context,
                fmwvtool: updated
              }
            });
          }
          
          // Queue sync if enabled
          if (updated.syncEnabled) {
            synkr.queueChange({
              id: crypto.randomUUID(),
              type: 'update',
              entityType: 'fmwvtool-config',
              data: updated,
              timestamp: Date.now(),
              source: 'local'
            });
          }

          return updated;
        }
      });

      setIsInitialized(true);
      loggr.info('✅ FMwvTool initialized');
    };

    init();
  }, [actr, profylr, aggregatr, integratr, synkr, loggr, activeProfile]);

  // Helper to read file
  const readFile = (file: File): Promise<string | ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result!);
      reader.onerror = () => reject(reader.error);

      if (file.type.startsWith('text/') || file.type === 'application/json') {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  };

  // Public API
  const value: FMwvToolContextValue = {
    loadFile: async (file: File) => {
      await actr.execute('fmwvtool:load-file', { file });
    },
    
    saveFile: async (data: any) => {
      if (!currentFile) {
        throw new Error('No file loaded');
      }
      const updated = { ...currentFile, ...data };
      await aggregatr.update('file', updated.id, updated);
      setCurrentFile(updated);
    },
    
    clearFile: async () => {
      await actr.execute('fmwvtool:clear-file', {});
    },
    
    extractJson: async (content: string) => {
      return await actr.execute('fmwvtool:extract-json', { content });
    },
    
    pingFileMaker: async () => {
      return await actr.execute('fmwvtool:ping-filemaker', {});
    },
    
    sendToFileMaker: async (data: any, script?: string) => {
      await actr.execute('fmwvtool:send-to-filemaker', { data, script });
    },
    
    getConfig: async () => {
      return await actr.execute('fmwvtool:get-config', {});
    },
    
    updateConfig: async (updates: any) => {
      return await actr.execute('fmwvtool:update-config', { updates });
    },
    
    currentFile,
    isInitialized
  };

  return (
    <FMwvToolContext.Provider value={value}>
      {children}
    </FMwvToolContext.Provider>
  );
}

/**
 * Hook to access FMwvTool functionality
 */
export function useFMwvTool(): FMwvToolContextValue {
  const context = useContext(FMwvToolContext);
  if (!context) {
    throw new Error('useFMwvTool must be used within FMwvToolProvider');
  }
  return context;
}
